import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'

export interface ComboboxOption {
  value: string
  /** Shown instead of `value` where the two differ. */
  label?: string
  /** How many things match this option, shown dim beside it. */
  count?: number
  /**
   * A glyph before the label.
   *
   * Added 2026-09-19 for the language chooser, which showed a flag on its trigger and two
   * bare words in its panel — so the control you were choosing from did not look like the
   * control you had chosen, and the one thing that identifies a language at a glance was
   * missing from the only place you need it.
   */
  icon?: ReactNode
}

export interface ComboboxProps {
  /** The accessible name. Rendered as a visible label unless `hideLabel`. */
  label: string
  hideLabel?: boolean
  options: ComboboxOption[]
  /**
   * Always an array, single-select included — a `value: string | null | string[]` union
   * would push the branch into every caller for no gain, and single-select is simply the
   * case where at most one entry is allowed.
   */
  value: string[]
  onChange: (values: string[]) => void
  /** Several at once. The button then summarises, and options toggle rather than replace. */
  multiple?: boolean
  /**
   * Show the filter field inside the panel. Defaults to **on above eight options**, which is
   * roughly where scanning a list stops being faster than typing at it.
   */
  searchable?: boolean
  /**
   * ── These four are REQUIRED, and that is the point ──────────────────────────────────
   *
   * They used to default to 'Any', 'Type to filter' and 'Nothing matches that'. An optional
   * prop with an English default is how a component renders English inside a Mongolian
   * page — invisibly, because no call site looks wrong and nothing fails. **The design
   * system ships label keys and never copy**; the words live in `i18n/locales/en.json`
   * under `ul.*` and the caller resolves them.
   *
   * `Combobox` cannot call `useT()` itself: `DirectoryFilters` renders it, and the garage
   * and the resort use that component without a `LocaleProvider` above them. A hook here
   * would crash two apps that have no language state yet.
   */
  /** What the button says with nothing selected — `ul.combobox.any`. */
  emptyLabel: string
  /** `ul.combobox.filter`. */
  searchPlaceholder: string
  /** Shown in the panel when the filter matches nothing — `ul.combobox.noMatch`. */
  noMatchLabel: string
  /** The multi-select panel's clear button — `ul.combobox.clear`, already interpolated. */
  clearLabel?: string
  /**
   * Which edge of the trigger the panel hangs from. `start` by default.
   *
   * The panel is `min-width: max-content` — it has to be, or an option the trigger
   * truncated is truncated again in the list — so it is routinely wider than the control
   * that opened it. Anchored to the left of a narrow trigger near the right of the screen,
   * that surplus lands on top of whatever is beside it. `end` grows it leftwards instead,
   * into the page rather than off it.
   */
  align?: 'start' | 'end'
  className?: string
  /** A small mark before the button's text — a flag, a pictogram. */
  icon?: React.ReactNode
  /**
   * The trigger's `id`, and anything describing it.
   *
   * For a form that wraps this in its own labelled field. A `<button>` is a labelable
   * element, so a `<label for>` pointing at the trigger is valid and clicking the label
   * opens the list — and `aria-describedby` is how the field's hint and its error get read
   * out with the control rather than only sitting under it.
   *
   * Added for `app_steward`'s forms, where a trade and a province are this control inside
   * the same `Field` wrapper every other control uses. The board passes neither: its filter
   * rail has no wrapper and `label` is the whole of the naming.
   */
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: true
}

/**
 * A `.ul-combobox` — a listbox on a button, with a filter field.
 *
 * ## Why this exists and `<select>` does not do
 *
 * A native `<select>` cannot be typed at, cannot show a count beside an option, cannot hold
 * a mark beside its label, and on multiple it renders a scrolling multi-line box that nobody
 * has ever enjoyed using. It also cannot be styled past a point: `appearance: none` gets you
 * the closed state and nothing at all about the open list, which the platform draws.
 *
 * So this is the pattern the WAI-ARIA authoring practices call an editable combobox with a
 * listbox popup, built on a button and a `[role="listbox"]`, and it is what every select in
 * Partners uses.
 *
 * ## What it gets right, because these are the parts that get skipped
 *
 * - **`role="combobox"` on the trigger** with `aria-expanded`, `aria-controls` and
 *   `aria-haspopup="listbox"`, so a screen reader announces a collapsed combobox rather
 *   than a button with some text in it.
 * - **`aria-activedescendant`, not focus**, for the active option. Focus stays on the field
 *   the person is typing in; moving real focus into the list is what breaks type-to-filter.
 * - **Full keyboard.** Down/Up move, Home/End jump, Enter selects, Escape closes and returns
 *   focus to the trigger, Tab closes and moves on. On multiple, Enter keeps the panel open,
 *   because choosing several things and having the list shut after the first is the single
 *   most irritating thing a multi-select can do.
 * - **`aria-selected` on every option**, set or not — a listbox that only marks the selected
 *   ones leaves a screen reader unable to say "not selected".
 * - **Click outside and Escape both close**, and the panel is not rendered at all when shut,
 *   so its controls are out of the tab order rather than merely invisible.
 */
export function Combobox({
  label,
  hideLabel = false,
  options,
  value,
  onChange,
  multiple = false,
  searchable,
  emptyLabel,
  searchPlaceholder,
  noMatchLabel,
  clearLabel,
  align = 'start',
  className,
  icon,
  id,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: ComboboxProps) {
  const uid = useId()
  const listId = `${uid}-list`
  const labelId = `${uid}-label`
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const field = useRef<HTMLInputElement>(null)

  // Eight is where scanning stops being faster than typing. A caller may say otherwise.
  const withSearch = searchable ?? options.length > 8

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q === '') return options
    return options.filter((o) => (o.label ?? o.value).toLowerCase().includes(q))
  }, [options, query])

  // Reset the filter and the active row every time the panel opens, so it never opens
  // showing the remains of the last search.
  useEffect(() => {
    if (!open) return
    setQuery('')
    setActive(0)
    // The field if there is one, the panel otherwise — something inside must take focus or
    // Escape has nothing to close from.
    const target = withSearch ? field.current : root.current?.querySelector('[role="listbox"]')
    if (target instanceof HTMLElement) target.focus()
  }, [open, withSearch])

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const close = (returnFocus = true) => {
    setOpen(false)
    if (returnFocus) trigger.current?.focus()
  }

  const choose = (optionValue: string) => {
    if (multiple) {
      onChange(
        value.includes(optionValue)
          ? value.filter((v) => v !== optionValue)
          : [...value, optionValue],
      )
      // Deliberately left open — see the note about choosing several things.
      return
    }
    onChange(value.includes(optionValue) ? [] : [optionValue])
    close()
  }

  /** What the trigger says. */
  const summary = (() => {
    if (value.length === 0) return emptyLabel
    const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v
    if (value.length === 1) return labelFor(value[0]!)
    // Naming two and counting the rest reads better than "3 selected" and much better than
    // a list that overflows the button.
    return `${labelFor(value[0]!)} +${value.length - 1}`
  })()

  const onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setActive((i) => (shown.length === 0 ? 0 : (i + 1) % shown.length))
        break
      case 'ArrowUp':
        event.preventDefault()
        setActive((i) => (shown.length === 0 ? 0 : (i - 1 + shown.length) % shown.length))
        break
      case 'Home':
        event.preventDefault()
        setActive(0)
        break
      case 'End':
        event.preventDefault()
        setActive(Math.max(0, shown.length - 1))
        break
      case 'Enter': {
        event.preventDefault()
        const option = shown[active]
        if (option) choose(option.value)
        break
      }
      case 'Escape':
        event.preventDefault()
        close()
        break
      case 'Tab':
        // Not prevented: Tab should move on, and leaving the panel open behind it would
        // strand a listbox on screen with focus somewhere else.
        setOpen(false)
        break
      default:
        break
    }
  }

  return (
    <div
      className={['ul-combobox', align === 'end' ? 'ul-combobox--align-end' : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      ref={root}
    >
      <span className={hideLabel ? 'ul-combobox-label visually-hidden' : 'ul-combobox-label'} id={labelId}>
        {label}
      </span>

      <button
        type="button"
        ref={trigger}
        id={id}
        className={value.length > 0 ? 'ul-combobox-trigger is-set' : 'ul-combobox-trigger'}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        onClick={() => setOpen((o) => !o)}
      >
        {icon && <span className="ul-combobox-icon">{icon}</span>}
        <span className="ul-combobox-value">{summary}</span>
        <i className="flaticon-down" aria-hidden="true" />
      </button>

      {open && (
        <div className="ul-combobox-panel">
          {withSearch && (
            <div className="ul-combobox-search">
              <input
                ref={field}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setActive(0)
                }}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder}
                aria-label={`${label} — ${searchPlaceholder}`}
                aria-controls={listId}
                aria-activedescendant={shown[active] ? `${uid}-o-${active}` : undefined}
                aria-autocomplete="list"
              />
              <i className="flaticon-search" aria-hidden="true" />
            </div>
          )}

          <ul
            className="ul-combobox-options"
            id={listId}
            role="listbox"
            aria-labelledby={labelId}
            aria-multiselectable={multiple || undefined}
            // Focusable only when there is no field to hold focus instead.
            tabIndex={withSearch ? -1 : 0}
            onKeyDown={withSearch ? undefined : onKeyDown}
            aria-activedescendant={!withSearch && shown[active] ? `${uid}-o-${active}` : undefined}
          >
            {shown.length === 0 && <li className="ul-combobox-empty">{noMatchLabel}</li>}
            {shown.map((option, i) => {
              const selected = value.includes(option.value)
              return (
                <li
                  key={option.value}
                  id={`${uid}-o-${i}`}
                  role="option"
                  // Set either way: a listbox that only marks the chosen ones leaves a
                  // screen reader unable to say "not selected".
                  aria-selected={selected}
                  className={
                    [
                      'ul-combobox-option',
                      selected ? 'is-selected' : '',
                      i === active ? 'is-active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')
                  }
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(option.value)}
                >
                  {multiple && (
                    <span className="ul-combobox-tick" aria-hidden="true">
                      <i className="flaticon-tick" />
                    </span>
                  )}
                  {option.icon && (
                    <span className="ul-combobox-option-icon" aria-hidden="true">
                      {option.icon}
                    </span>
                  )}
                  <span className="ul-combobox-option-label">{option.label ?? option.value}</span>
                  {option.count !== undefined && (
                    <span className="ul-combobox-option-count">{option.count}</span>
                  )}
                </li>
              )
            })}
          </ul>

          {multiple && value.length > 0 && (
            <div className="ul-combobox-foot">
              <button type="button" onClick={() => onChange([])}>
                {clearLabel ?? label}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
